import { TranslateService } from '@ngx-translate/core';
import moment from 'moment';
import { ChartSerie } from './chart-serie';
import { ClickNotifier } from './click-notifier';
import { ChartPoint } from './chart-point';

export abstract class HighChartDataBase {

    /**
     * Totala summan av alla staplar/värden
    */
    public seriesTotal: number;

    public chartWidth: number;
    public chartHeight: number;
    public chartType: string;
    public reportName: string;
    public chartTitle: string;
    public chartSubTitle: string;
    public yAxisTitle: string;
    public yAxisTitleColor: string;
    public hasY2Axis: boolean;
    public y2AxisTitle: string;
    public y2AxisTitleColor: string;
    public xAxisTitle: string;
    public xAxisColors: string[];
    public xAxisLabelRotation: number;
    public credits: string;
    public creditsLink: string;
    public sqlString: string;
    public chartSeries: ChartSerie[] = [];
    public categories: any[] = [];
    public customDataLabelsTitle: string;
    public customDataLabels: string[];
    public startDate: moment.Moment;
    public endDate: moment.Moment;
    public grouping: string;
    public groupings: string[]; // Alla tillgängliga grupperingar för denna rapport
    public reportType: string;
    public isModified: boolean;
    public displayUnit: string;
    public detailedTable: TableModelBase;
    public showTotal: boolean;
    public showMedian: boolean;
    public showAverage: boolean;
    public yAxisDecimals: number;
    public y2AxisDecimals: number;
    public descriptionHtml: string;
    public targetLevel?: number;
    public targetLine?: number;
    public targetLineWidth?: number;
    public tooltips: string[][];
    public xMinValue: number;
    public xMaxValue: number;
    public yMinValue: number;
    public yMaxValue: number;
    public y2MinValue: number;
    public y2MaxValue: number;

    public skipParams: string[];

    public defaultColors: string[];

    public abstract getHighChartOptions(): Object;


    constructor(
        protected translate: TranslateService,
        protected jsonData?: any, 
        protected width?: any, 
        protected height?: any, 
        protected clickNotifier?: ClickNotifier
    ) {
        this.chartWidth = width || 600;
        this.chartHeight = height || 400;
        if (jsonData) {
            this.reportName = jsonData['ReportName'];
            this.chartType = jsonData['ChartType'];
            this.chartTitle = jsonData['ChartTitle'];
            this.chartSubTitle = jsonData['ChartSubTitle'];
            this.yAxisTitle = jsonData['YaxisTitle'];
            this.yAxisTitleColor = jsonData['YaxisTitleColor'];
            this.hasY2Axis = jsonData['HasY2Axis'];
            this.y2AxisTitle = jsonData['Y2axisTitle'];
            this.y2AxisTitleColor = jsonData['Y2axisTitleColor'];
            this.xAxisTitle = jsonData['XaxisTitle'];
            this.credits = jsonData['Credits'];
            this.creditsLink = jsonData['CreditsLink'];
            this.sqlString = jsonData['SqlString'];
            this.categories = jsonData['Categories'];
            if (this.categories && this.categories.length > 0 && this.isDateTime(this.categories[0]))
                this.categories = this.categoriesTypeToDateTime();
            this.customDataLabelsTitle = jsonData['DataLabelsTitle'];
            this.customDataLabels = jsonData['DataLabels'];

            if (jsonData['StartDate'] != null && jsonData['StartDate'].length > 0)
                this.startDate = moment.utc(jsonData['StartDate']);
            if (jsonData['EndDate'] != null && jsonData['EndDate'].length > 0)
                this.endDate = moment.utc(jsonData['EndDate']);

            this.grouping = jsonData['Grouping'];
            this.groupings = jsonData['Groupings'];
            this.isModified = jsonData['IsModified'];
            this.displayUnit = jsonData['DisplayUnit'];
            if (jsonData['DetailedTable'] != null)
                this.detailedTable = new TableModelBase(jsonData['DetailedTable'].Columns, jsonData['DetailedTable'].Rows, jsonData['DetailedTable'].Colors, jsonData['DetailedTable'].Alignments);

            this.showTotal = jsonData['ShowTotal'];
            this.showAverage = jsonData['ShowAverage'];
            this.showMedian = jsonData['ShowMedian'];
            this.yAxisDecimals = jsonData['YAxisDecimals'];
            this.y2AxisDecimals = jsonData['Y2AxisDecimals'];
            this.descriptionHtml = jsonData['DescriptionHtml'];
            this.targetLevel = jsonData['TargetLevel'];
            this.targetLine = jsonData['TargetLine'];
            this.targetLineWidth = jsonData['TargetLineWidth'];
            this.tooltips = jsonData['Tooltips'];

            this.skipParams = jsonData['SkipParams'];

            let chartColors = jsonData['ChartColors'];
            this.defaultColors = (chartColors && chartColors.length > 0) ? chartColors : ["#53a8c3", "#ee5773", "#f0d86e", "#f6854f", "#15ceac", "#5773ee", "#f597a9", "#0e8771", "#aa7ecd", "#a6a6a6", "#2ecc6f", "#644f8e", "#99c4e0", "#ffc18a", "#a50000"];

            this.xMinValue = jsonData['XMinValue'] != "NaN" ? jsonData['XMinValue'] : Number.NaN;
            this.xMaxValue = jsonData['XMaxValue'] != "NaN" ? jsonData['XMaxValue'] : Number.NaN;
            this.yMinValue = jsonData['YMinValue'] != "NaN" ? jsonData['YMinValue'] : Number.NaN;
            this.yMaxValue = jsonData['YMaxValue'] != "NaN" ? jsonData['YMaxValue'] : Number.NaN;
            this.y2MinValue = jsonData['Y2MinValue'] != "NaN" ? jsonData['Y2MinValue'] : Number.NaN;
            this.y2MaxValue = jsonData['Y2MaxValue'] != "NaN" ? jsonData['Y2MaxValue'] : Number.NaN;

            if (jsonData['ChartSeries'] && jsonData['ChartSeries'].length > 0) {
                jsonData['ChartSeries'].forEach((serie) => {
                    this.chartSeries.push(this.loadSerie(serie));
                });
            }
            this.seriesTotal = jsonData['SeriesTotal'];
            if (this.chartSeries[0])
                this.xAxisLabelRotation = window.screen.width < 768 ? -60 : (this.categories.length > 12 ? -55 : 0);

            this.xAxisColors = jsonData['XaxisColors'];
            this.countColorsVsCategories();
            this.calculateMinAndMaxValue();
            this.calculateOneValueSeries();
        }
    }

    private loadSerie(jsonData: any): ChartSerie {
        let output = new ChartSerie();
        output.name = jsonData['Name'];
        output.stack = jsonData['Stack'];
        output.type = jsonData['Type'];
        output.color = jsonData['Color'];
        output.serieTotal = jsonData['SerieTotal'];
        output.serieAverage = jsonData['SerieAverage'];
        output.serieMedian = jsonData['SerieMedian'];
        output.yAxis = jsonData['YAxis'];
        if (jsonData['Data'] && jsonData['Data'].length > 0) {
            let firstDataItem = jsonData['Data'][0];
            if (firstDataItem['StartValue']) output.data = this.calculateRangeSerieData(jsonData['Data']); // chartType 'rangecolumn'
            else if (this.isDateTime(firstDataItem['Name'])) output.data = this.calculateXAxisDateSerieData(jsonData['Data']);  // x-values has type datetime
            else output.data = this.calculateSerieData(jsonData['Data']);
            
            if (firstDataItem['Color'] == "Transparent") {
                let serieIsTransparent = true;
                jsonData['Data'].forEach(dataItem => {
                    if (dataItem['Color'] != "Transparent") { 
                        serieIsTransparent = false;
                        return;
                    }
                });
                if (serieIsTransparent) {
                    output.color = "Transparent";
                    output.showInLegend = false;
                }
            }
        }
        if (jsonData['Color'] == "Transparent") output.showInLegend = false;
        return output;
    }

    /**
     * Beräknar en serie staplar/linjer etc utifrån serverdatan
     * @param jsonData
     */
    private calculateSerieData(jsonChartPointData: any[]): ChartPoint[] {
        let output: ChartPoint[] = [];
        jsonChartPointData.forEach((point) => {
            output.push(new ChartPoint(point));
        });
        return output;
    };

    /*
     * Sätter serier med endast ett värde till "marker" disabled
     */
    private calculateOneValueSeries(): void {
        this.chartSeries.forEach((serie: any) => {
            if (serie.data.length == 0) return;
            if (serie.data.length == 1) {
                serie.marker = { enabled: false };
            } else {
                var dataLastIndex = serie.data.length-1;
                var dataMiddleIndex = Math.round(serie.data.length/2);
                // If first, middle and last value is the same, check if the whole serie has the same value
                if (serie.data[0].y == serie.data[dataLastIndex].y && 
                    serie.data[0].y == serie.data[dataMiddleIndex].y && 
                    this.lineHasOneValue(serie)) 
                {
                    // Disable markers if serie contains one value only
                    serie.marker = { enabled: false };
                }
            }
        });
    }
    /*
     * Kontrollerar om någon serie innehåller endast ett värde
     */
    private lineHasOneValue(serie: ChartSerie): boolean {
        let lineHasOneValue = false;
        let startValue = serie.data[0].y;
        serie.data.forEach((value: ChartPoint) => {
            if (value.y != startValue) {
                lineHasOneValue = false;
                return;
            } else {
                lineHasOneValue = true;
            }
        });
        return lineHasOneValue;
    }

    /**
     * Beräknar det absoluta min- & max-värdet i datat
     */
    public seriesMinValue: number = 0;
    public seriesMaxValue: number = 0;
    public seriesContainsDecimals: boolean = false;
    private calculateMinAndMaxValue(): void {
        let minValue = 0;
        let maxValue = 0;
        this.chartSeries.forEach((serie) => {
            serie.data.forEach((value) => {
                if (value.y > maxValue)
                    maxValue = value.y;
                if (value.y < minValue)
                    minValue = value.y;
                if (value.y % 1 != 0)
                    this.seriesContainsDecimals = true;
            });
        });
        this.seriesMinValue = minValue;
        this.seriesMaxValue = maxValue;
    }

    /*
     * Beräknar om diagrammet har min >= 0 och max == 100 
     * => Andelsgraf
     */
    public minIsPositiveMaxIsHundred(): boolean {
        let minIsPositive = false;
        if (Number.isNaN(this.yMinValue) && this.seriesMinValue >= 0) {
            minIsPositive = true;
        } else if (this.yMinValue == 0) {
            minIsPositive = true;
        }        
        let maxIsHundred = false;
        if (Number.isNaN(this.yMaxValue) && this.seriesMaxValue == 100) {
            maxIsHundred = true;
        } else if (this.yMaxValue == 100) {
            maxIsHundred = true;
        }
        return minIsPositive && maxIsHundred;
    }

    /**
     * Beräknar en serie rangecolumn etc utifrån serverdatan
     * @param jsonData
     */
    private calculateRangeSerieData(jsonChartPointData: any[]): any[] {
        let output = [];
        jsonChartPointData.forEach((point) => {
            output.push({
                //point['Name'], 
                high: this.isDateTime(point['StartValue']) ? Date.parse(this.createDateString(point['StartValue'])) : point['StartValue'], 
                low: this.isDateTime(point['EndValue']) ? Date.parse(this.createDateString(point['EndValue'])) : point['EndValue'],
                color: point['Color']
            });
        });
        return output;
    };

    /**
     * Beräknar en serie med datumvärden utifrån serverdatan
     * @param jsonData
     */
    private calculateXAxisDateSerieData(jsonChartPointData: any[]): ChartPoint[] {
        let output: ChartPoint[] = [];
        jsonChartPointData.forEach((point) => {
            let newPoint = new ChartPoint(point);
            let dateString = this.createDateString(newPoint.name);
            newPoint.name = new Date(dateString);
            output.push(newPoint);
        });
        return output;
    }

    /**
     * Beräknar kategoriers datumvärden
     */
    private categoriesTypeToDateTime(): any[] {
        let output = [];
        this.categories.forEach((category: string) => {
            let dateString = this.createDateString(category);
            output.push(new Date(dateString));
        });
        return output;
    }

    private createDateString(date: string): string {
        return date.substring(0, 10) + "T" + date.substring(11) + "+01:00";
    }

    /**
     * If there is more textcolors than categories, 
     * remove colors from top til' there's as many colors as categories
     */
    private countColorsVsCategories() {
        if (this.xAxisColors) {
            let nCategories = this.categories.length;
            let nTextColors = this.xAxisColors.length;
            if (nCategories < nTextColors)
                this.xAxisColors.splice(0, nTextColors-nCategories);
        } else {
            this.xAxisColors = [];
        }
    }

    /**
     * Skapa default options för en Y-axel 
     */
    public createYAxis(
        yTitle: string, yTitleColor: string, 
        yMinValue: number, yMaxValue: number, 
        opposite = false, 
        labelsFormatter = undefined,
        endOnTick = true, tickAmount = undefined) : Object 
    {
        let yAxisOptions = {
            title: {
                min: 0,
                text: yTitle,
                style: {
                    color: yTitleColor ? yTitleColor : "#666666"
                }
            },
            plotLines: [{
                value: this.targetLine,
                color: '#4f678f',
                dashStyle: 'LongDash',
                width: this.targetLineWidth,
                zIndex: 5 // framför staplar Och ticks-linjer 
            }],
            labels: {
                formatter: labelsFormatter
            },
            min: Number.isNaN(yMinValue) ? (this.seriesMinValue >= 0 && this.seriesMaxValue <= 1 ? 0 : null) : yMinValue,
            max: Number.isNaN(yMaxValue) ? (this.seriesMinValue >= 0 && this.seriesMaxValue <= 1 ? 1 : null) : yMaxValue,
            endOnTick: endOnTick,
            tickAmount: tickAmount,
            allowDecimals: this.seriesContainsDecimals,
            opposite: opposite
        };
        return yAxisOptions;
    }

    public isDateTime(datevalue): boolean {
        let isValidDate = moment(datevalue, "YYYY-MM-DD HH:mm:ss", true).isValid();
        let isTrustableDate = moment(datevalue, "YYYY-MM-DD HH:mm:ss") > moment("2000-01-01 00:00:00");
        return isValidDate && isTrustableDate;
    }

    public getDefaultTooltipText(tooltip, shared: boolean, momentFormat: string = "YYYY-MM-DD HH:mm:ss"): string {
        let text = '<u style="font-size:11px">';
        if (this.chartType == "line" || this.chartType == "funnel") {
            text += `${this.xAxisTitle}: `;
        }
        if (this.isDateTime(tooltip.key)) { 
            text += moment(tooltip.key).locale(this.translate.currentLang).format(momentFormat); 
        }
        else { 
            text += tooltip.key; 
        }
        text += '</u><table>';
        
        let hasMultipleSeries = this.chartSeries.length > 1;

        if (tooltip.points != undefined) {
            tooltip.points.forEach((point) => {
                if (point.color != "Transparent") {
                    let symbol = this.getPointSymbol(point.series);
                    let value = this.formatValue(point.y);
                    text += hasMultipleSeries ? `<tr><td style="padding:0 5px 0 0; font-weight:500"><i style="color: ${point.series.color}">${symbol}</i></td><td>${point.series.name}:&ensp;</td>` : "<tr>";
                    text += `<td><b>${value} ${this.displayUnit}</b></td></tr>`;
                }
            });
        } else if (tooltip.point != undefined) {
            let symbol = this.getPointSymbol(tooltip.point.series);
            let value = this.formatValue(tooltip.point.y);
            if (hasMultipleSeries || this.chartType == "line" || this.chartType == "funnel") {
                text += `<tr><td style="padding:0 5px 0 0; font-weight:500"><i style="color: ${tooltip.series.color}">${symbol}</i></td><td>${tooltip.series.name}:&ensp;</td>`;
            } else {
                text += "<tr>"
            }
            text += `<td><b>${value} ${this.displayUnit}</b></td></tr>`;
        }
        text += "</table>";
        
        let tooltipIndex = -1;
        for (let i = 0; i < this.chartSeries.length; i++) {
            for (let j = 0; j < this.chartSeries[i].data.length; j++) {
                let dataItem = this.chartSeries[i].data[j];
                let dataName = dataItem.name;
                if (dataName) {
                    if (dataName == tooltip.key) {
                        tooltipIndex = j;
                        break;
                    }
                } else if (dataItem[0] == tooltip.key && 
                          ((dataItem[1] != undefined && dataItem[1] == tooltip.y) || dataItem[1] == undefined)) {
                    tooltipIndex = j;
                    break;
                }
            }
            if (tooltipIndex > -1)
                break;
        }
       
        let pointTooltips: string[] = this.tooltips[tooltipIndex];
        if (pointTooltips) {
            if (!shared) { 
                //Shared tooltip = false
                let serieIndex = tooltip.series.index;
                if (pointTooltips[serieIndex]) {
                    let tooltipText = pointTooltips[serieIndex];
                    text += "<table><tr><td>&ensp;</td></tr><tr>";
                    text += hasMultipleSeries ? "<td>&ensp;</td>" : "";
                    text += `<td style="white-space: pre-line;">${tooltipText}</td></tr>`;
                    text += "</table>";
                }
            } else { 
                //Shared tooltip = true
                for (var i = 0; i < pointTooltips.length; i++) {
                    if (pointTooltips[i]) {
                        if (i === 0) text += "<table><tr><td>&ensp;</td></tr><tr>";
                        text += hasMultipleSeries ? "<td>&ensp;</td>" : "";
                        text += `<td style="white-space: pre-line;">${pointTooltips[i]}</td></tr>`;
                        if (i === pointTooltips.length-1) text += "</table>";
                    }
                }
            }
        }

        return text;
    }

    private formatValue(value) {
        return Number.isNaN(value) ? value : new Intl.NumberFormat(this.translate.currentLang, { maximumFractionDigits: this.yAxisDecimals, minimumFractionDigits: this.yAxisDecimals }).format(value);
    }

    public getPointSymbol(serie): string {
        let symbol = '';
        switch ( serie.symbol ) {
            case 'circle':
                symbol = '●';
                break;
            case 'diamond':
                symbol = '♦';
                break;
            case 'square':
                symbol = '■';
                break;
            case 'triangle':
                symbol = '▲';
                break;
            case 'triangle-down':
                symbol = '▼';
                break;
            default:
                symbol = '●';
                break;
        }
        return symbol;
    }

}

export class TableModelBase {
    constructor(
        public columns: string[],
        public rows: string[][],
        public colors: string[][],
        public alignments: string[][]
    ) { }
}
